import {
  sendLoginRequest,
  sendLogoutRequest,
  sendRefreshTokenRequest,
} from '@lmt/unicorn-shared/app/api/auth/auth-client';
import { useEffect, useRef } from 'react';
import { QueryFactory, UserClaims } from '@lmt/shared-types';
import { getDispatch } from '../../store/types';
import { logout } from '../../store/actions';
import {
  convertPermissionsFromServer,
  permissionsAreEqual,
} from '../../api/auth/token-decoder';
import { createId } from '../../helpers/create-id';
/*
 * According to 'Task 34771: Auto logout on Unicorn closing' we need to log the user out when Unicorn is closed.
 * We decided to use browser Session to determine if Unicorn was closed or not.
 *
 * Since this requirement regarding logging user out might change (or get adjusted, e.g. log out depending on some user preference),
 * it doesn't make sense to change the place where access/refresh token is stored.
 *
 * So we just save the following Key in Session storage (when user logs in), and log the user out if this Key isn't present in Session.
 */
export const AuthSessionKey = '_loggedIn';

export async function login(username: string, password: string) {
  await sendLoginRequest(username, password);
}

/*
 * Sends logout request to backend.
 * Should be done when user somehow manually wants to logout.
 */
export async function logOut() {
  // show loading?
  await sendLogoutRequest();

  cleanupOnLogout();
}

export async function cleanupOnLogout() {
  getDispatch()(logout());
  _logoutHandler();
}

export function useIsAuthorized(): boolean | 'loading' {
  const data = QueryFactory.UserQuery.useGetCurrentUserInfoQuery({
    throwOnError: false,
    retryOnMount: false,
    retry: false,
  });
  if (data?.data?.id) return true;

  return data.isError ? false : data.isLoading ? 'loading' : false;
}

export function useClaims(): UserClaims {
  const data = QueryFactory.UserQuery.useGetCurrentUserInfoQuery({
    throwOnError: false,
    retryOnMount: false,
    retry: false,
  });
  return data.data
    ? {
        ...data.data,
        subtenants: data.data.subtenants ?? [],
        permissions: convertPermissionsFromServer(data.data.permissions ?? []),
      }
    : {
        id: '',
        name: '',
        nickname: '',
        permissions: {} as any,
        subtenants: [],
      };
}

const _logoutHandlers: Record<string, () => void> = {};
const _logoutHandler = () => {
  Object.values(_logoutHandlers).forEach((x) => x());
};

export function addLogoutHandler(handler: () => void) {
  const id = createId();
  _logoutHandlers[id] = handler;
  return () => {
    delete _logoutHandlers[id];
  };
}

export function useRefreshTokensWhenPermissionsChange() {
  const query = QueryFactory.UserQuery.useGetCurrentUserInfoQuery({
    throwOnError: false,
    retryOnMount: false,
    retry: false,
  });
  const currentPermissionsRef = useRef(query.data);
  useEffect(() => {
    if (!query.data) return;
    if (!currentPermissionsRef.current) {
      currentPermissionsRef.current = query.data;
      return;
    }
    if (
      !permissionsAreEqual(
        currentPermissionsRef.current as any,
        query.data as any,
      )
    ) {
      void sendRefreshTokenRequest();
    }
  }, [query.data]);
}
